import type { StepPlanInfoId, StepPlanRepetitionMode } from '@az3fr/core';
import { useCallback } from 'react';

import { exerciseSliceSelectors } from '@/entities/exercise/model/exercise.selector';
import { sectionPlanSliceSelectors } from '@/entities/section-plan/model/section-plan.selector';
import { stepPlanSliceSelectors } from '@/entities/step-plan/model/step-plan.selector';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';

export function useStepEditorLogic(stepPlanId: StepPlanInfoId) {
	const dispatch = useAppDispatch();

	const stepPlan = useAppSelector((state) => stepPlanSliceSelectors.selectById(state, stepPlanId));
	const sectionPlan = useAppSelector((state) =>
		sectionPlanSliceSelectors.selectById(state, stepPlan.sectionPlanId),
	);
	const exercise = useAppSelector((state) =>
		exerciseSliceSelectors.selectPopulatedExerciseById(state, stepPlan.exerciseId ?? ''),
	);

	const handleNoteChange = useCallback(
		(notes: string | null) => {
			dispatch(
				workoutCreatorThunks.updateStepPlanProperties({
					stepPlanId,
					changes: { notes },
				}),
			);
		},
		[dispatch, stepPlanId],
	);

	const switchRepetitionMode = useCallback(
		(newRepetitionMode: StepPlanRepetitionMode) => {
			if (newRepetitionMode === stepPlan.repetitionMode) {
				return;
			}

			if (newRepetitionMode === 'explicit') {
				dispatch(
					workoutCreatorThunks.switchToExplicitRepetitionModeForStepPlan({
						stepPlanId,
					}),
				);
			} else {
				dispatch(
					workoutCreatorThunks.switchToTemplateRepetitionModeForStepPlan({
						stepPlanId,
					}),
				);
			}
		},
		[dispatch, stepPlanId, stepPlan.repetitionMode],
	);

	return {
		stepPlan,
		sectionPlan,
		exercise,
		handleNoteChange,
		switchRepetitionMode,
	};
}
